import Phaser from 'phaser';
import { generateHoles, findHole, HOLE_RADIUS, PLAY_MARGIN_X, type Hole } from '../game/Playfield';
import { createTargetGlow } from '../game/HoleRenderer';
import { HoleSpriteRenderer } from '../game/HoleSpriteRenderer';
import { getLevelLayout, NUM_LEVELS } from '../state/levelLayout';
import { LOGICAL_HEIGHT, LOGICAL_WIDTH, WORLD_HEIGHT, RENDER_SCALE } from '../dimensions';
import { gameState } from '../state/GameState';
import { Bar, BAR_THICKNESS } from '../game/Bar';
import { Ball, BALL_RADIUS, BALL_START_X } from '../game/Ball';
import { stepRolling, rollingToWorld, type RollingState } from '../game/rolling';
import { InputBindings } from '../game/InputBindings';
import type { TouchSnapshot } from '../game/InputController';
import { createWalls } from '../game/Walls';
import { detectCapture } from '../game/HoleDetector';
import { fireBallFallIn } from '../game/BallFallIn';
import { tokens } from '../design/tokens';
import { audio } from '../game/Audio';
import { createBackground } from '../game/Background';
import { showTutorialOverlay } from './TutorialOverlay';
import { attachDebugBridge } from '../debug/debugBridge';
import { BURN_EVENT, type BurnTransitionPayload } from './BurnTransitionScene';
import { TrippyController } from '../game/effects/TrippyController';
import { StarfieldHolesController } from '../game/effects/StarfieldHolesController';

const TARGET_PULL_RADIUS_FACTOR = 2.0;   // applies when ball is within this × HOLE_RADIUS of target
const TARGET_PULL_MAX_SPEED = 120;       // px/s; nudge only applies when ball is below this speed
const TARGET_PULL_STRENGTH = 0.12;       // per-step velocity nudge, delta-normalised to 60fps

export class GameScene extends Phaser.Scene {
  private holes: ReadonlyArray<Hole> = [];
  private target!: Hole;
  private targetGlow?: Phaser.GameObjects.Image;
  private holeRenderer?: HoleSpriteRenderer;
  private bar!: Bar;
  private ball!: Ball;
  private inputBindings!: InputBindings;
  private isResolving = false;
  private clearCells: Set<string> = new Set();
  private starfieldHoles?: StarfieldHolesController;
  private trippy?: TrippyController;
  private rollingState: RollingState = { x: BALL_START_X, vx: 0 };

  private readonly LEFT_X  = PLAY_MARGIN_X;
  private readonly RIGHT_X = LOGICAL_WIDTH - PLAY_MARGIN_X;
  private readonly BAR_LENGTH = LOGICAL_WIDTH - 2 * PLAY_MARGIN_X;

  constructor() { super('Game'); }

  create(): void {
    // Anchor camera at top-left + zoom by RENDER_SCALE so the canvas backing-store is
    // RENDER_SCALEx the logical viewport while world coords stay at LOGICAL_WIDTH×LOGICAL_HEIGHT.
    // Skip cameras.setBounds — Phaser's built-in clamp assumes origin (0.5, 0.5); scrollY is
    // already clamped manually below.
    this.cameras.main.setOrigin(0, 0).setZoom(RENDER_SCALE);
    this.matter.world.setBounds(0, 0, LOGICAL_WIDTH, WORLD_HEIGHT);
    this.scene.launch('Hud');

    this.holes = generateHoles();
    this.starfieldHoles = new StarfieldHolesController(this, () => this.getBallWorldPos());
    this.trippy = new TrippyController(this);
    createBackground(this, { fullWorld: true });
    // Phaser's bundled Matter types omit `gravity` on the Engine interface; runtime is fine.
    (this.matter.world.engine as unknown as { gravity: { scale: number } }).gravity.scale = 0.0040;

    createWalls(this);
    this.bar = new Bar(this);
    this.ball = new Ball(this);
    this.inputBindings = new InputBindings(this, this.bar);

    this.loadLevel();

    this.game.events.on('lavascend:mute', (m: boolean) => audio.setMuted(m));
    this.events.once('shutdown', () => this.game.events.off('lavascend:mute'));
    this.events.once('shutdown', () => {
      this.holeRenderer?.destroy();
      this.holeRenderer = undefined;
    });
    this.events.once('shutdown', () => {
      this.trippy?.destroy();
      this.trippy = undefined;
    });
    this.events.once('shutdown', () => {
      this.starfieldHoles?.destroy();
      this.starfieldHoles = undefined;
    });

    this.input.keyboard?.on('keydown-ESC', () => {
      gameState.reset();
      this.scene.stop('Hud');
      this.scene.start('Title');
    });

    if (!gameState.hasPlayed()) {
      showTutorialOverlay(this, () => gameState.markPlayed());
    }

    attachDebugBridge(this);
  }

  private loadLevel(fresh = true): void {
    this.isResolving = false;
    this.trippy?.setGated(false);
    const layout = getLevelLayout(gameState.level);
    this.clearCells = layout.clearCells;
    this.target = findHole(this.holes, layout.target.col, layout.target.row);

    // Hole rendering: re-window for the new clear-cell set; only the first call constructs sprites.
    if (this.holeRenderer) {
      this.holeRenderer.setClearCells(this.clearCells);
    } else {
      this.holeRenderer = new HoleSpriteRenderer(this, this.holes, this.clearCells, -5);
    }

    this.targetGlow?.destroy();
    this.targetGlow = createTargetGlow(this, this.target);
    this.targetGlow.setDepth(-4);

    this.trippy?.setLevel(gameState.level);
    this.starfieldHoles?.setHoles(this.target, this.clearCells);

    this.bar.reset();
    this.ball.reset();
    this.rollingState = { x: BALL_START_X, vx: 0 };
    this.inputBindings.reset();
    this.game.events.emit('lavascend:state');
    if (fresh) audio.levelStart();
  }

  update(_time: number, delta: number): void {
    const barCenterY = (this.bar.leftAnchorY + this.bar.rightAnchorY) / 2;
    const targetScrollY = barCenterY - LOGICAL_HEIGHT * 0.65;
    this.cameras.main.scrollY = Phaser.Math.Clamp(targetScrollY, 0, WORLD_HEIGHT - LOGICAL_HEIGHT);

    this.inputBindings.update(delta);

    // Compute slope from current anchors and step the 1D rolling sim (unless resolving a
    // win/fail animation). Ball is glued to the bar; world (x, y) derived from rollingState.x.
    const dy = this.bar.rightAnchorY - this.bar.leftAnchorY;
    const L  = this.BAR_LENGTH;
    const D  = Math.sqrt(L * L + dy * dy);
    const sinT = dy / D;
    const cosT = L  / D;
    const xMin = this.LEFT_X  + BALL_RADIUS;
    const xMax = this.RIGHT_X - BALL_RADIUS;

    if (!this.isResolving) {
      this.rollingState = stepRolling(this.rollingState, delta, sinT, cosT, xMin, xMax, tokens.physics.ball);
      const world = this.rollingBallWorld();
      this.ball.setWorldPosition(world.x, world.y);
    }

    this.bar.update();
    this.starfieldHoles?.tick(delta);
    this.trippy?.tick(delta);

    if (this.isResolving) return;

    const world = this.rollingBallWorld();
    this.maybeApplyTargetPull(delta, world.x, world.y);

    const result = detectCapture(
      { x: world.x, y: world.y, speed: Math.abs(this.rollingState.vx) },
      this.holes,
      this.target,
      this.clearCells,
    );
    if (result.kind === 'win')       this.resolveCapture('win',  result.hole);
    else if (result.kind === 'fail') this.resolveCapture('fail', result.hole);
  }

  private resolveCapture(kind: 'win' | 'fail', hole: Hole): void {
    this.isResolving = true;
    this.trippy?.setGated(true);
    this.inputBindings.setEnabled(false);
    if (kind === 'win') audio.win(); else audio.death();
    if (kind === 'fail') {
      gameState.lives.decrement();
      this.game.events.emit('lavascend:lives');
    }
    const totalMs = kind === 'win' ? tokens.motion.capture.winMs : tokens.motion.capture.failMs;
    fireBallFallIn(this, this.ball, hole, kind, totalMs, () => this.afterCapture(kind));
  }

  private afterCapture(kind: 'win' | 'fail'): void {
    if (kind === 'win') {
      const payload: BurnTransitionPayload = {
        onMidpoint: () => {
          if (gameState.level >= NUM_LEVELS) {
            gameState.recordRun(NUM_LEVELS);
            gameState.reset();
            this.game.events.emit('lavascend:state');
            this.scene.stop('Hud');
            this.scene.start('Title');
            return;
          }
          gameState.level += 1;
          this.game.events.emit('lavascend:state');
          this.loadLevel();
        },
      };
      this.game.events.emit(BURN_EVENT, payload);
      return;
    }
    // fail
    if (!gameState.lives.exhausted) {
      this.loadLevel(false);
      return;
    }
    const finalLevel = gameState.level;
    audio.gameOver();
    const payload: BurnTransitionPayload = {
      onMidpoint: () => {
        gameState.recordRun(finalLevel);
        gameState.reset();
        this.game.events.emit('lavascend:state');
        this.scene.stop('Hud');
        this.scene.start('Title');
      },
      holdText: { title: 'GAME OVER', subtitle: `LEVEL ${finalLevel}` },
    };
    this.game.events.emit(BURN_EVENT, payload);
  }

  private maybeApplyTargetPull(deltaMs: number, ballX: number, ballY: number): void {
    if (this.isResolving || !this.target) return;
    const dx = this.target.x - ballX;
    const dy = this.target.y - ballY;
    const d = Math.hypot(dx, dy);
    if (d < 1e-3) return;
    if (d > HOLE_RADIUS * TARGET_PULL_RADIUS_FACTOR) return;
    if (Math.abs(this.rollingState.vx) > TARGET_PULL_MAX_SPEED) return;
    const step = TARGET_PULL_STRENGTH * (deltaMs / 16.67);
    // Only nudge vx — ball's y is enforced by the bar.
    this.rollingState.vx += (dx / d) * step;
  }

  private getBallWorldPos(): { x: number; y: number } {
    if (!this.ball) return { x: 0, y: 0 };
    return { x: this.ball.x(), y: this.ball.y() };
  }

  private rollingBallWorld(): { x: number; y: number } {
    return rollingToWorld(
      this.rollingState.x, this.bar.leftAnchorY, this.bar.rightAnchorY,
      this.LEFT_X, this.BAR_LENGTH, BALL_RADIUS, BAR_THICKNESS
    );
  }

  public debugGetBarState(): { leftAnchorY: number; rightAnchorY: number } {
    return { leftAnchorY: this.bar.leftAnchorY, rightAnchorY: this.bar.rightAnchorY };
  }
  public debugGetBallState(): { x: number; y: number; speed: number } {
    const world = this.rollingBallWorld();
    return { x: world.x, y: world.y, speed: Math.abs(this.rollingState.vx) };
  }
  public debugGetLevel(): number { return gameState.level; }
  public debugGetBest(): number { return gameState.best; }
  public debugGetLives(): number { return gameState.lives.remaining; }
  public debugSetLives(n: number): void {
    const clamped = Math.max(0, Math.min(4, Math.floor(n)));
    if (gameState.lives.remaining < clamped) {
      // Only way up is reset, so reset then drain to the target.
      gameState.lives.reset();
    }
    while (gameState.lives.remaining > clamped) gameState.lives.decrement();
    this.game.events.emit('lavascend:lives');
  }
  public debugForceGameOver(): void {
    // Drain to one remaining life, then trigger a normal fail to flow through
    // the live resolveCapture('fail', …) path (private method — we don't call it directly).
    gameState.lives.reset();
    for (let i = 0; i < 3; i++) gameState.lives.decrement();
    this.game.events.emit('lavascend:lives');
    this.debugPlaceBallAtTrap();
  }
  public debugPlaceBallAtTarget(): void {
    this.debugPlaceBallAtHole(this.target);
  }
  public debugPlaceBallAtTrap(): void {
    const trap = this.holes.find(h => h.index !== this.target.index)!;
    this.debugPlaceBallAtHole(trap);
  }
  public debugInspectHoles(): { type: string; poolSize: number } | null {
    if (!this.holeRenderer) return null;
    return { type: 'HoleSpriteRenderer', poolSize: this.holeRenderer.debugPoolSize() };
  }
  public debugStarfieldHolesActive(): boolean {
    return this.starfieldHoles?.isActive() ?? false;
  }
  public debugJumpToLevel(level: number): void {
    gameState.level = Math.max(1, Math.min(NUM_LEVELS, Math.floor(level)));
    this.game.events.emit('lavascend:state');
    this.loadLevel();
  }

  // Snap the bar flat at the hole's row, then place the rolling state at the hole's x.
  // rollingToWorld will then produce world coords centred on the hole, and detectCapture
  // will trigger on the next update tick.
  private debugPlaceBallAtHole(hole: Hole): void {
    const barY = hole.y + BAR_THICKNESS / 2 + BALL_RADIUS;
    this.bar.leftAnchorY  = barY;
    this.bar.rightAnchorY = barY;
    this.inputBindings.reset();
    this.rollingState = { x: hole.x, vx: 0 };
    this.bar.update();
    const world = rollingToWorld(
      this.rollingState.x, this.bar.leftAnchorY, this.bar.rightAnchorY,
      this.LEFT_X, this.BAR_LENGTH, BALL_RADIUS, BAR_THICKNESS
    );
    this.ball.setWorldPosition(world.x, world.y);
  }
  public debugTriggerTrip(type: 'invert' | 'aberration' | 'acid' | 'vignette'): void {
    this.trippy?.trigger(type);
  }

  public getJoystickSnapshots(): TouchSnapshot[] {
    return this.inputBindings ? this.inputBindings.getActiveTouchSnapshots() : [];
  }
}
